<template>
  <div>
    <div v-if="loading">
      <loader />
    </div>
    <div v-if="!loading && !isErrorState">
      <StageSelector
        :is-update="isUpdate"
        @save-from-ai-translation="saveFromAiTranslation"
        @save-from-manual-translation="saveFromManualTranslation"
        @save="save"
      />
      <BuildStage
        v-show="activeStage === 0"
        :loading="loading"
        :last-retrieved-at="lastRetrievedAt"
        :resource-id="resourceId"
        :is-cloning="isCloning"
        :validation-errors="validationErrors"
      />
      <SettingsStage
        v-show="activeStage === 1"
        class="mt-24"
        :is-update="isUpdate"
        :resource-id="resourceId"
      />
      <ReportStage
        v-show="activeStage === 2"
        class="mt-24"
        :resource-id="resourceId"
        :is-cloning="isCloning"
        :is-update="isUpdate"
      />
    </div>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import { Errors } from 'laravel-nova';
  import BuildStage from '@/_legacy/views/ChecklistBuilder/Build/Build.vue';
  import SettingsStage from '@/_legacy/views/ChecklistBuilder/Settings/Settings.vue';
  import ReportStage from '@/_legacy/views/ChecklistBuilder/Report/Report.vue';
  import StageSelector from '@/_legacy/views/ChecklistBuilder/StageSelector.vue';
  import { eventBus } from '@/_legacy/eventBus';
  import { useTask } from '@/ui/concerns';
  import { create, get, update, updateStatus, updateTranslations } from '@/services/api/resources/checklist';
  import { getLanguages } from '@/services/api/resources/organization/actions';
  import { list as listEntityTypes } from '@/services/api/resources/entityType';
  import { entityFolderDefaultQuery, list as listEntityFolders } from '@/services/api/resources/entityFolder';
  import { isEmpty } from 'lodash/fp';
  import { gtm, parseInt10, toasted, sequence, reload } from '@/ui/helpers';
  import { filter as userFilter } from '@/services/api/resources/user';
  import { EventBus } from '@/ui/EventBus';
  import { Events } from '@/ui/events';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import { t } from '@/ui/i18n/i18n';
  import { StatusCodes } from 'http-status-codes';
  import * as checklistAssetsFiles from '@/services/assets-api/checklistAssetsFiles';
  import { undeletedActions } from '@/ui/pages/checklist/ChecklistBuilderIssueTemplateStore';
  import { undeletedChecklists } from '@/ui/pages/checklist/ChecklistBuilderChecklistTemplateStore';
  import { handleIssueTemplates } from '@/services/issueTemplate/checklistBuiler';
  import { handleChecklistTemplates } from '@/services/checklistTemplate/checklistBuiler';
  import { v4 as uuidv4 } from 'uuid';
  import { logicConditionAcceptsEmptyValue } from '@/_legacy/services/checklist';
  import { trackEvent, EventTypes, EVENTS } from '@/services/activityTracker/activityTrackerHelper';
  import { getFileExtension, getFileType } from '@/services/assets-api/assets-helper';
  import { extToMime } from '@/services/file/fileProcessor';
  import { Userflow, UserflowTrackEvent, UserflowTrackEventData } from '@/services/userflow/userflow';
  import { LanguageTranslationStatus, SessionStorageKeys } from '@/entities/checklist';
  import { entityTypeDefaultQuery } from '@/services/api/resources/entityType/query';
  import { kebabCase } from 'lodash';
  import { Permissions } from '@/ui/auth/auth.types';
  import { removeNodeIdFromItem } from '@/_legacy/helpers/inspectionNodeIdTransformer';
  import { retrieveEntityItems } from '@/services/entityItem/entityItemService';
  import { defaultEntityItemQuery } from '@/services/api/resources/entityItem/query';
  import { useEntityItemsStore } from '@/stores/conduct/entityItems';
  import { save as saveNotifiables } from '@/services/api/resources/checklistNotifiable';
  import { useChecklistNotifiablesStore } from '@/stores/checklist/checklistNotifiables';
  import { useTranslationLanguagesStore } from '@/stores/checklist/translationLanguages';
  import { useManualTranslationsStore } from '@/stores/checklist/manualTranslations';
  import { hasAiBalance, isFormTranslationsFeatureEnabled, user } from '@/ui/auth/auth';

  const getConditionValues = condition => {
    if (Array.isArray(condition.value)) {
      return condition.value;
    }

    if (condition.value === undefined || condition.value === null) {
      return [];
    }

    return [condition.value];
  };

  const conditionAllowsEmptyValue = condition => {
    const { operator } = condition;

    return logicConditionAcceptsEmptyValue(operator);
  };

  const existsInvalidLogic = item => {
    if (item.conditions) {
      for (const condition of item.conditions) {
        if (getConditionValues(condition).length === 0 && !conditionAllowsEmptyValue(condition)) {
          return true;
        }
      }
    }

    if (item.items) {
      for (const childItem of item.items) {
        const exists = existsInvalidLogic(childItem);

        if (exists) {
          return true;
        }
      }
    }

    return false;
  };

  export default {
    components: {
      StageSelector,
      BuildStage,
      SettingsStage,
      ReportStage,
    },
    provide() {
      return {
        isTranslationEnabled: computed(() => this.isTranslationEnabled),
        isAiTranslationMode: computed(() => this.isAiTranslationMode),
        isManualTranslationMode: computed(() => this.isManualTranslationMode),
        isCurrentLangInProgress: computed(() => {
          const lang = this.translationLanguagesStore.getLanguage(this.checklist?.translations_locale);

          if (!lang) {
            return false;
          }

          return lang.aiEnabled
            ? [LanguageTranslationStatus.pending, LanguageTranslationStatus.inProgress].includes(lang.status)
            : lang.status === LanguageTranslationStatus.inProgress;
        }),
      };
    },
    props: {
      resourceId: {
        type: String,
      },
      templateId: {
        type: String,
      },
      isCreatedFromAi: {
        type: Boolean,
        default: false
      },
      isCreatedFromPrompt: {
        type: Boolean,
        default: false
      },
      isCreatedFromFileUpload: {
        type: Boolean,
        default: false
      },
      isCloning: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      const translationLanguagesStore = useTranslationLanguagesStore();
      const manualTranslationsStore = useManualTranslationsStore();

      return {
        translationLanguagesStore,
        manualTranslationsStore,
      };
    },
    data: () => ({
      stages: {
        BuildStage: {
          active: true,
        },
        SettingsStage: {
          active: false,
        },
      },
      //todo: set loading from outside as property or convert this component to TS
      loading: false,
      lastRetrievedAt: null,
      validationErrors: new Errors(),
      isErrorState: false,
    }),
    computed: {
      isUpdate() {
        if (this.resourceId === '' && !this.isCloning) {
          return false;
        }

        return this.resourceId && !this.isCloning;
      },
      isTranslationEnabled() {
        return isFormTranslationsFeatureEnabled.value
          && (!!this.checklist?.form?.node_id)
          && this.isUpdate;
      },
      isAiTranslationMode() {
        return this.translationLanguagesStore.isAiEnabledLanguageSelected && this.isTranslationEnabled;
      },
      isManualTranslationMode() {
        return this.translationLanguagesStore.isManualLanguageSelected && this.isTranslationEnabled;
      },
      isCreateFromTemplate() {
        return !!this.templateId;
      },

      checklist() {
        return this.$store.getters['checklist_builder/checklist'];
      },

      categories() {
        return this.$store.getters['checklist_builder/categories'];
      },

      reportTemplates() {
        return this.$store.getters['checklist_builder/report_templates'];
      },

      users() {
        return this.$store.getters['checklist_builder/users'];
      },

      schedules() {
        return this.$store.getters['checklist_builder/schedules'];
      },

      groups() {
        return this.$store.getters['checklist_builder/groups'];
      },

      entityItems() {
        return this.$store.getters['checklist_builder/entityItems'];
      },

      roles() {
        return this.$store.getters['checklist_builder/roles'];
      },

      activeStage() {
        return this.$store.getters['checklist_builder/activeStage'];
      },

      isEditingReportTemplate() {
        return this.$store.getters['checklist_builder/edited_report_template'] !== null;
      }
    },
    watch: {
      templateId() {
        this.initBuilder().then(() => {
          this.fetchApprovalUsers();
          this.fetchUsers();
          this.fetchActionApprovalUsers();
          this.fetchLanguages();
        });
      },
    },
    destroyed() {
      EventBus.$off(Events.userCreated, this.onUserCreated);

      const translationStore = useTranslationLanguagesStore();
      translationStore.stopPolling();
      translationStore.$reset();

      const manualTranslationsStore = useManualTranslationsStore();
      manualTranslationsStore.$reset();
    },
    created() {
      EventBus.$on(Events.userCreated, this.onUserCreated);

      this.initBuilder().then(() => {
        this.fetchApprovalUsers();
        this.fetchUsers();
        this.fetchActionApprovalUsers();
        this.fetchLanguages();

        this.$store.commit('checklist_builder/setIsUpdating', this.isUpdate);
      });
    },
    methods: {
      existsEmptyConditionLogic() {
        return existsInvalidLogic(this.$store.getters['checklist_builder/checklist'].form);
      },
      loadedData(data) {
        if (isEmpty(data)) {
          return;
        }

        if (this.isUpdate || this.isCloning) {
          if (data.checklist && data.checklist.form.params.format_version !== 2) {
            eventBus.$emit(
              'error',
              this.$t(
                'Incompatible form version: {formVersion} instead of {systemVersion}',
                {
                  formVersion: data.checklist.form.params.format_version,
                  systemVersion: 2,
                },
              ),
            );
            return;
          }

          data.checklist.form = this.isCloning ? removeNodeIdFromItem(data.checklist.form) : data.checklist.form;

          this.$store.dispatch(
            'checklist_builder/loadTemplate',
            data.checklist,
          );
        } else if (this.isCreateFromTemplate) {
          this.$store.dispatch(
            'checklist_builder/loadTemplate',
            data.template,
          );
        } else if (this.isCreatedFromAi && data.template) {
          this.$store.dispatch('checklist_builder/loadTemplate', data.template);
        } else {
          this.$store.commit('checklist_builder/resetBuilder');
          this.$store.commit('checklist_builder/loadDefaultForm');
        }

        if (this.isUpdate && data._retrievedAt) {
          this.lastRetrievedAt = data._retrievedAt;
        }

        // set state
        this.$store.commit('checklist_builder/loadUsers', data.users);
        this.$store.commit('checklist_builder/loadGroups', data.groups);
        this.$store.commit('checklist_builder/loadEntityItems', data.entity_items);
        this.$store.commit('checklist_builder/loadCategories', data.categories);
        this.$store.commit('checklist_builder/loadRoles', data.roles);
        this.$store.commit('checklist_builder/setCanSeeAllEntityItems', data.can_see_all_entity_items);

        if (this.isUpdate) {
          this.manualTranslationsStore.setChecklistId(this.resourceId);
        }
        this.loadEntityTypes();
        this.loadEntityFolders();
        this.handleLanguageQueryParam();
        this.setupTranslationPolling();
      },
      updatedData(response, redirect = true) {
        if (!isEmpty(response) && response.status === StatusCodes.OK) {
          this.$store.commit('checklist_builder/updateSubmittedState', false);

          this.$toasted.show(this.$t('The checklist was saved!'), { type: 'success' });

          if (redirect) {
            this.$router.push({
              name: 'forms',
              query: {
                withTour: '1',
              },
            });
          }
        } else {
          this.$store.commit('checklist_builder/updateSubmittedState', false);

          if (response.status === StatusCodes.UNPROCESSABLE_ENTITY) {
            this.validationErrors = new Errors(response.parsed.data.errors);
          }

          if (response.status === StatusCodes.CONFLICT) {
            this.$toasted.show(
              this.$t('Another user has updated this checklist since this page was loaded. Please refresh the page and try again.'),
              { type: 'error' },
            );
          }
        }
      },
      async onUserCreated({
        response,
        identifier,
      }) {
        toasted.success(t('user.created'), { duration: 5000 });

        await this.fetchUsers();
        await this.fetchApprovalUsers();
        await this.fetchActionApprovalUsers();

        const payload = {
          groups: [],
          users: [],
          entity_items: [],
        };

        if (this.checklist.restrictions && this.checklist.restrictions.users) {
          payload.users = [...this.checklist.restrictions.users, response.id];
        }

        if (this.checklist.restrictions && this.checklist.restrictions.groups) {
          payload.groups = [...this.checklist.restrictions.groups];
        }

        if (this.checklist.restrictions && this.checklist.restrictions.entity_items) {
          payload.entity_items = [...this.checklist.restrictions.entity_items];
        }

        if (CreateUserModelIdentifier.checklistBuilderUserRestriction === identifier) {
          this.$store.commit('checklist_builder/setChecklistRestrictions', payload);
        }
      },
      async fetchUsers() {
        const getUsers = useTask(
          () => userFilter(),
          response => {
            const payload = response.data.reduce((acc, current) => {
              acc[current.id] = current.title;
              return acc;
            }, {});

            this.$store.commit('checklist_builder/loadUsers', payload);
          },
        );
        return getUsers.run();
      },
      async fetchApprovalUsers() {
        const getUsers = useTask(
          () => userFilter({
            permission: [Permissions.APPROVE_ALL_INSPECTIONS, Permissions.APPROVE_ASSIGNED_INSPECTIONS].map(kebabCase)
          }),
          response => {
            const payload = response.data.reduce((previous, current) => ({
              ...previous,
              [current.id]: current.title,
            }), {});
            this.$store.commit('checklist_builder/loadApprovalUsers', payload);
          },
        );
        return getUsers.run();
      },

      async fetchActionApprovalUsers() {
        const getApprovalUsers = useTask(
          () => userFilter({
            permission: [Permissions.APPROVE_ALL_ACTIONS, Permissions.APPROVE_ASSIGNED_ACTIONS].map(kebabCase)
          }),
          response => {
            const payload = response.data.reduce((previous, current) => ({
              ...previous,
              [current.id]: current.title,
            }), {});
            this.$store.commit('checklist_builder/loadApprovalActionUsers', payload);
          },
        );
        return getApprovalUsers.run();
      },

      async fetchLanguages() {
        const getOrganizationLanguages = useTask(
          () => getLanguages(user.value.organization.id),
          languages => {
            this.$store.commit('checklist_builder/loadLanguages', languages.data);
          },
        );
        return getOrganizationLanguages.run();
      },

      setupTranslationPolling() {
        const translationLanguages = this.checklist?.fullTranslationLanguages || [];

        this.translationLanguagesStore.initialize(translationLanguages);

        if (!this.isUpdate || !isFormTranslationsFeatureEnabled.value || translationLanguages.length === 0) {
          return;
        }

        if (this.translationLanguagesStore.hasIncompleteLanguages) {
          this.translationLanguagesStore.startPolling(parseInt(this.resourceId, 10));
        }
      },

      handleLanguageQueryParam() {
        const languageId = this.getLanguageParam();

        if (languageId) {
          this.translationLanguagesStore.setSelectedLanguageId(parseInt(languageId, 10));

          const urlParams = new URLSearchParams(window.location.search);
          urlParams.delete('language');
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      },

      getLanguageParam() {
        const urlParams = new URLSearchParams(window.location.search);

        return urlParams.get('language');
      },

      initBuilder() {
        this.$store.commit('checklist_builder/resetBuilder');
        this.$store.commit('checklist_builder/loadDefaultForm');
        return this.getData();
      },
      getData() {
        this.loading = true;

        let getRecord = null;
        if (this.isUpdate || this.isCloning) {
          const query = defaultEntityItemQuery();
          const translationLang = this.getLanguageParam();
          getRecord = useTask(
            () => sequence(
              translationLang ? get(parseInt(this.resourceId), translationLang) : get(parseInt(this.resourceId)),
              retrieveEntityItems(query),
            ),
            ([data, entities]) => {
              const store = useEntityItemsStore();
              store.setEntityItems(query, entities);
              this.loadedData(data.parsed);
              this.loading = false;
            },
            uuidv4(),
            e => {
              this.isErrorState = true;
              this.loading = false;

              if (e.code === 404) {
                this.$router.push({ name: 'notFound' });
              }

              if (e.code === 403) {
                this.$router.push({ name: 'forbidden' });
              }
            },
          );
        } else if (this.isCreateFromTemplate) {
          getRecord = useTask(
            () => create({ fromTemplate: parseInt10(this.templateId) }),
            data => {
              this.loadedData(data.parsed);
              this.loading = false;
              gtm.dataLayer()
                .push({
                  event: 'templateCreated',
                });
            },
          );
        } else if (this.isCreatedFromAi) {
          getRecord = useTask(
            create,
            data => {
              const presavedTemplate = sessionStorage.getItem(SessionStorageKeys.AiTemplate);
              if (presavedTemplate) {
                data.parsed.template = JSON.parse(presavedTemplate);
              }
              this.loadedData(data.parsed);
              this.loading = false;
              gtm.dataLayer()
                .push({
                  event: 'aiTemplateCreated',
                });
            },
          );
        } else {
          getRecord = useTask(
            create,
            data => {
              this.loadedData(data.parsed);
              this.loading = false;
              gtm.dataLayer()
                .push({
                  event: 'templateCreated',
                });
            },
          );
        }

        return getRecord.run();
      },
      async loadEntityTypes(page = 1, append = false) {
        const query = {
          ...entityTypeDefaultQuery(),
          page
        };

        useTask(
          () => listEntityTypes(query),
          resp => {
            if (resp.meta.current_page < resp.meta.last_page) {
              this.loadEntityTypes(resp.meta.current_page + 1, true);
            }

            if (append) {
              this.$store.commit('checklist_builder/appendEntityTypes', resp.data);

              return;
            }

            this.$store.commit('checklist_builder/loadEntityTypes', resp.data);
          },
        ).run();
      },
      async loadEntityFolders() {
        useTask(
          () => listEntityFolders(entityFolderDefaultQuery()),
          resp => {
            this.$store.commit('checklist_builder/loadEntityFolders', resp.data);
          },
        ).run();
      },
      trackFileEvent(file, templateId) {
        const ext = getFileExtension(file);
        trackEvent(EventTypes.templateFileAttached, {
          ...EVENTS.ATTACH_FILE,
          file_name: file,
          file_extension: ext,
          file_mime: extToMime(ext),
          file_type: getFileType(file),
          template_id: templateId,
        });
      },
      getSaveResponseId(response) {
        return response.parsed?.id;
      },
      reloadToLanguage(languageId) {
        const param = languageId > 0 ? { key: 'language', value: String(languageId) } : null;
        reload(param);
      },
      async save({ status, languageId }) {
        const isLanguageReload = languageId !== undefined;

        if (this.translationLanguagesStore.isManualLanguageSelected) {
          const successCallback = () => {
            toasted.success(t('The checklist was saved!'));
            this.reloadToLanguage(languageId);
          };
          this.saveFromManualTranslation({ status }, successCallback);

          return;
        }

        if (this.existsEmptyConditionLogic()) {
          eventBus.$emit('error', t('checklist.builder.empty-logic'));
          return;
        }

        if (this.isEditingReportTemplate) {
          let resolveCallback = null;
          const saveEditingReportTemplate = new Promise(resolve => {
            resolveCallback = resolve;
          });

          eventBus.$emit(Events.saveReportTemplate, resolveCallback);

          const valid = await saveEditingReportTemplate;

          if (!valid) {
            return;
          }
        }

        this.$store.commit('checklist_builder/addChecklistState', status);
        await this.$store.dispatch('checklist_builder/beforeSave');

        const basePayload = {
          ...this.$store.getters['checklist_builder/checklist'],
        };

        if (basePayload.wordTemplateId) {
          basePayload.report_template = null;
        } else if (basePayload.report_template) {
          basePayload.wordTemplateId = null;
        }

        try {
          this.$store.commit('checklist_builder/updateSubmittedState', true);
          this.loading = true;
          const actionsToHandle = undeletedActions.value;
          const checklistsToHandle = undeletedChecklists.value;

          const issueTemplateResponses = await handleIssueTemplates(actionsToHandle);
          const checklistTemplateResponses = await handleChecklistTemplates(checklistsToHandle);

          const { files } = await checklistAssetsFiles.processPhotos(basePayload);

          basePayload.issueTemplates = issueTemplateResponses.idsSync;
          basePayload.checklistTemplates = checklistTemplateResponses.idsSync;

          const updateCallback = response => {
            this.updatedData(response, !isLanguageReload);
            this.loading = false;

            if (!this.isUpdate) {
              Userflow.trackEvent(UserflowTrackEvent.TEMPLATE_CREATED, { [UserflowTrackEventData.TEMPLATE_NAME]: basePayload.checklist_title });
            }

            const templateId = this.getSaveResponseId(response);
            const { notifiables } = useChecklistNotifiablesStore();
            saveNotifiables(templateId, notifiables)();

            files.forEach(file => this.trackFileEvent(file, templateId));

            if (isLanguageReload) {
              this.reloadToLanguage(languageId);
            }
          };

          this.saveRequest(
            this.resourceId,
            this.lastRetrievedAt,
            basePayload,
            this.isUpdate,
            updateCallback,
          );

          toasted.success(t('checklist.save-success'));

          if (!isLanguageReload) {
            this.$emit('saved', { status });
          }

          if (
            !hasAiBalance.value
            && isFormTranslationsFeatureEnabled.value
            && Array.isArray(this.checklist.fullTranslationLanguages)
            && this.checklist.fullTranslationLanguages.length > 0
          ) {
            this.$toasted.show(this.$t('ai.credits-exhausted'), { type: 'error' });
          }
        } catch (e) {
          this.$store.commit('checklist_builder/updateSubmittedState', false);
          this.loading = false;
        }
      },
      saveRequest(resourceId, lastRetrievedAt, basePayload, isUpdate, updateCallback) {
        const formData = new FormData();

        formData.append('checklist', JSON.stringify(basePayload));
        formData.append('fromTemplate', String(this.templateId || 0));
        formData.append('fromAiPrompt', this.isCreatedFromAi ? 'true' : 'false');
        formData.append('fromFileUpload', this.isCreatedFromFileUpload ? 'true' : 'false');

        let updateRecord = null;
        if (isUpdate) {
          formData.append('_method', 'PUT');
          formData.append('_retrieved_at', lastRetrievedAt);
          updateRecord = useTask(
            () => update(formData, resourceId),
            updateCallback,
          );
        } else {
          updateRecord = useTask(
            () => update(formData),
            updateCallback,
          );
        }

        updateRecord.run();
      },
      saveFromAiTranslation({ status }) {
        const action = () => updateStatus(parseInt(this.resourceId, 10), status);
        this.saveFromTranslation(action);
      },
      saveFromManualTranslation({ status }, successCallback) {
        const action = () => updateTranslations(
          parseInt(this.resourceId, 10),
          this.translationLanguagesStore.selectedLanguageId,
          this.manualTranslationsStore.getPayload(status),
        );
        this.saveFromTranslation(action, successCallback);
      },
      saveFromTranslation(action, successCallback) {
        this.loading = true;
        const task = useTask(
          action,
          successCallback ?? (() => {
            toasted.success(t('The checklist was saved!'));
            this.$router.push({ name: 'forms', query: { withTour: '1' } });
          }),
          uuidv4(),
          e => {
            toasted.error(e.message);
            console.error(e);
            this.loading = false;
          }
        );
        task.run();
      }
    },
  };
</script>
